/**
 * Alles, was sich aus dem ?data=-Parameter der Seite lesen laesst, OHNE die
 * signierte URL aufzurufen.
 *
 * pdfGenerator (aivy-backend-app) haengt hier eine base64-kodierte S3-URL an,
 * die 15 Minuten gueltig ist. Ihr Key traegt die Sprache des Reports:
 *
 *   private/pdfs/<language>/user/<user_id>/<TYPE>.json
 *
 * Das ist die einzige Sprachinformation, die der Seite bleibt, wenn der Link
 * abgelaufen ist und das JSON nicht mehr geladen werden kann. Genau dann
 * braucht die Statusseite sie aber: Wer einen deutschen Report angefordert
 * hat, soll auch die Meldung dazu auf Deutsch lesen.
 */

export const SUPPORTED_LANGUAGES = ["de", "en"];

const DEFAULT_LANGUAGE = "de";

/* Das Backend kennt nl, die Seite hat aber keine niederlaendischen Texte.
   Englisch ist fuer diese Leser die naehere Zweitsprache als Deutsch. */
const LANGUAGE_FALLBACKS = { nl: "en" };

/**
 * "de", "en-GB", "NL" -> "de" | "en" | null
 */
export const normalizeLanguage = (value) => {
  if (typeof value !== "string") return null;

  const code = value.toLowerCase().split("-")[0];
  if (SUPPORTED_LANGUAGES.includes(code)) return code;

  return LANGUAGE_FALLBACKS[code] || null;
};

/* X-Amz-Date kommt als 20260909T155827Z, ohne Trennzeichen — das versteht
   new Date() nicht. */
const parseAmzDate = (value) => {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(
    value || "",
  );
  if (!match) return null;

  const [, year, month, day, hour, minute, second] = match;

  return new Date(
    Date.UTC(+year, +month - 1, +day, +hour, +minute, +second),
  );
};

/**
 * @param {string} search - window.location.search
 * @returns {{ dataUrl: string, language: string|null, signedAt: Date|null,
 *   expiresAt: Date|null } | null} null, wenn kein brauchbarer Link da ist
 */
export const parseReportLink = (search) => {
  const encoded = new URLSearchParams(search).get("data");
  if (!encoded) return null;

  let dataUrl;
  try {
    dataUrl = atob(encoded);
  } catch {
    return null;
  }

  let url;
  try {
    url = new URL(dataUrl);
  } catch {
    return null;
  }

  const languageMatch = /\/private\/pdfs\/([a-z]{2})\//i.exec(url.pathname);
  const signedAt = parseAmzDate(url.searchParams.get("X-Amz-Date"));
  const expiresIn = Number(url.searchParams.get("X-Amz-Expires"));
  const expiresAt =
    signedAt && Number.isFinite(expiresIn) && expiresIn > 0
      ? new Date(signedAt.getTime() + expiresIn * 1000)
      : null;

  return {
    dataUrl,
    language: languageMatch ? languageMatch[1].toLowerCase() : null,
    signedAt,
    expiresAt,
  };
};

/**
 * Sprache fuer die Statusseite: erst der Link (das ist die Sprache, in der der
 * Report angefordert wurde), dann der Browser, zuletzt Deutsch.
 */
export const detectLanguage = ({ link, navigatorLanguages = [] } = {}) => {
  const fromLink = normalizeLanguage(link?.language);
  if (fromLink) return fromLink;

  for (const candidate of navigatorLanguages) {
    const normalized = normalizeLanguage(candidate);
    if (normalized) return normalized;
  }

  return DEFAULT_LANGUAGE;
};

/**
 * Wie lange der Link schon abgelaufen ist — fuer Bugsnag, nicht fuer die
 * Anzeige. Unterscheidet "gerade eben" (Recruiter hat den Tab kurz liegen
 * lassen) von "vor einer Woche" (alter Tab, Lesezeichen, weitergeleitet).
 *
 * @returns {number|null} Minuten, negativ solange der Link noch gilt
 */
export const minutesSinceExpiry = (link, now = new Date()) => {
  if (!link?.expiresAt) return null;

  return Math.round((now.getTime() - link.expiresAt.getTime()) / 60000);
};

/* Wieviel die Uhr des Geraets falsch gehen darf, bevor wir ihr mehr glauben
   als dem naheliegenden Grund fuer einen 403. */
const CLOCK_SKEW_MINUTES = 5;

/**
 * Sagt der Link selbst, dass er noch gelten muesste?
 *
 * S3 antwortet mit 403 nicht nur auf eine abgelaufene Signatur, sondern auch
 * auf eine falsche und auf eine geaenderte Bucket-Policy. Kommt der 403,
 * obwohl die Frist im Link noch laeuft, ist das kein abgelaufener Link,
 * sondern ein Ausfall — und der darf nicht im erwarteten Rauschen
 * verschwinden. Die Toleranz faengt eine falsch gehende Uhr ab; ohne
 * Zeitangaben im Link (false) bleibt es beim naheliegenden Grund.
 */
export const looksStillValid = (link, now = new Date()) => {
  const minutes = minutesSinceExpiry(link, now);

  return minutes !== null && minutes < -CLOCK_SKEW_MINUTES;
};

/* Dieselbe Zuordnung wie in aivy-backend-app
   sharedCode/integrationControl/index.js (dashboardUrl). REACT_APP_USER_BRANCH
   ist der Amplify-Branch: production, staging, develop. */
const DASHBOARD_URLS = {
  production: "https://dashboard.aivy.app",
  staging: "https://staging.dashboard.aivy.app",
  develop: "https://develop.dashboard.aivy.app",
};

export const getDashboardUrl = (env) =>
  DASHBOARD_URLS[env] || DASHBOARD_URLS.production;
